/**
 * 墙上时钟的读写 —— 3.5 #11「所有时间按商家的浏览器时间」的地基。
 *
 * ## 2026-09-10:换算那两个函数已经删掉了
 *
 * `browserWallToStoreWall` / `storeWallToBrowserWall` 存在的前提是「库里存店铺墙上时钟、
 * 屏幕上显示浏览器墙上时钟」。那个前提没有了:发布器现在也按**商家自己的表**解释
 * scheduled_date + scheduled_time(chatbot-api 的 allTenantZones),所以全链路一块表 ——
 * 商家选的 9:00 原样存、原样显示、原样发出去,没有任何一步需要换算。
 *
 * 删掉它们不是清理洁癖:那两个函数的两端各需要有人说对一次时区,而说错**从不抛异常**,
 * 只会显示一个看起来很合理的错时刻。2026-09-08~10 那次差一小时就是这么来的。
 * 少一层换算就少一个说错的机会。
 *
 * 下面留下的是**读**的那一半(某个时刻在某个时区是几点、某个墙上时钟是哪个时刻),
 * 那些仍然有人用,而且不涉及两个时区之间的往返。
 *
 * ## 为什么反向那一步要试两次
 *
 * 「浏览器时刻 → 某时区的墙上时钟」是容易的(`Intl` 直接格式化)。反过来
 * 「某时区的墙上时钟 → 真实时刻」没有现成 API:同一串 "2026-03-08 02:30" 在不同
 * 时区对应不同的时刻,而那个时区当天的偏移量又取决于……那个时刻本身(夏令时)。
 * 所以先用一个猜测的时刻求出该地当时的偏移,再据此修正一次 —— 修正后再验一遍,
 * 落在 DST 折叠/跳跃的缝里就退回猜测值,宁可差一小时也不返回 NaN。
 */

/** 一串墙上时钟:年月日 + 时分,不带时区。 */
export interface WallClock {
  /** 'YYYY-MM-DD' */
  date: string;
  /** 'HH:mm' */
  time: string;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** 某个真实时刻在指定时区的墙上时钟。tz 省略 = 浏览器本地。 */
export function wallClockIn(at: Date, tz?: string | null): WallClock {
  if (!tz) {
    return {
      date: `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`,
      time: `${pad(at.getHours())}:${pad(at.getMinutes())}`,
    };
  }
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      // h23 而不是 hour12:false —— 后者在部分实现里把午夜给成 "24"。
      hourCycle: 'h23',
    }).formatToParts(at);
    const get = (t: string) => parts.find(x => x.type === t)?.value ?? '';
    const [y, m, d, h, min] = ['year', 'month', 'day', 'hour', 'minute'].map(get);
    if (!y || !m || !d || !h || !min) return wallClockIn(at, null);
    return { date: `${y}-${m}-${d}`, time: `${h}:${min}` };
  } catch {
    // 无法识别的时区名(旧浏览器 / 脏数据)不该让调用方拿到垃圾。
    return wallClockIn(at, null);
  }
}

/** 墙上时钟字符串 → 数字。不合法返回 null(调用方据此放弃换算,而不是算出个错时间)。 */
function partsOf(wall: WallClock): [number, number, number, number, number] | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(wall.date.trim());
  const t = /^(\d{1,2}):(\d{2})$/.exec(wall.time.trim());
  if (!d || !t) return null;
  const nums: [number, number, number, number, number] = [
    Number(d[1]),
    Number(d[2]),
    Number(d[3]),
    Number(t[1]),
    Number(t[2]),
  ];
  return nums.some(n => Number.isNaN(n)) ? null : nums;
}

/**
 * 指定时区的墙上时钟 → 真实时刻。tz 省略 = 按浏览器本地解释。
 *
 * 见文件头「为什么反向那一步要试两次」。
 */
export function instantOfWallClock(wall: WallClock, tz?: string | null): Date | null {
  const p = partsOf(wall);
  if (!p) return null;
  const [y, m, d, h, min] = p;
  const asLocal = new Date(y, m - 1, d, h, min);
  if (!tz) return asLocal;
  // 第一次猜:把它当本地时刻,看它在目标时区显示成几点,差多少就补多少。
  const guessWall = wallClockIn(asLocal, tz);
  const gp = partsOf(guessWall);
  if (!gp) return asLocal;
  const guessAsLocal = new Date(gp[0], gp[1] - 1, gp[2], gp[3], gp[4]);
  const corrected = new Date(asLocal.getTime() + (asLocal.getTime() - guessAsLocal.getTime()));
  // 修正后再验一遍。DST 折叠/跳跃的缝里对不上,就退回第一次的猜测 ——
  // 宁可差一小时,也不把一个 NaN 或者差一天的时刻写进排期。
  const check = wallClockIn(corrected, tz);
  return check.date === wall.date && check.time === wall.time ? corrected : asLocal;
}

/** 浏览器时区的 IANA 名,给「all times in …」这类标注用。 */
export function browserTimezoneName(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'your local time';
  } catch {
    return 'your local time';
  }
}
